import * as React from 'react';
import { useLocale, usePermissions } from 'react-admin';
import { Paper } from '@material-ui/core';
import { CurrencyAutocomplete, MaterialTable } from '~/components';
import i18n from '~/i18n';
import * as hb from '~/types/Handbook';
import { validateUnique, createHelperText } from './utils';
import { useApi } from '~/hooks';
import { CurrencyType, getCurrencies } from '~/api/currencies';

const CurrencyTable = ({
  handbook,
  setState,
}: {
  handbook: Partial<hb.Handbook>;
  setState: hb.SetState;
}) => {
  const locale: keyof any = useLocale();
  const allCurrencies: { [key: string]: string } = (i18n as any)[locale].currencies;

  const currencies: hb.Currency[] = (handbook.currencies || []).slice();
  const [{ data, loading }] = useApi<CurrencyType[]>(getCurrencies);
  const options = data && data.map((item) => item.code);

  const { permissions } = usePermissions();
  if (!permissions) return null;

  return (
    <MaterialTable
      columns={[
        {
          title: 'Currency',
          field: 'id',
          required: true,
          validate: validateUnique(currencies, 'id', true),
          render: (rowData: hb.Currency) => (
            <>
              <span style={{ minWidth: '50px', display: 'inline-block' }}>{rowData.id}</span>
              <span
                style={{
                  opacity: 0.5,
                }}
              >
                {allCurrencies[rowData.id]}
              </span>
            </>
          ),
          width: 400,
        },
      ]}
      components={{
        Container: (props: any) => <Paper variant='outlined'>{props.children}</Paper>,
        EditField: (props: any) => {
          const errorMessage = props.columnDef.validate
            ? props.columnDef.validate(props.rowData)
            : null;
          const helperText = createHelperText(errorMessage);
          return (
            <CurrencyAutocomplete
              value={props.value || ''}
              label={props.columnDef.title}
              onChange={(event) => {
                props.onChange(event.target.value);
              }}
              options={options}
              disabled={loading}
              required={props.columnDef.required}
              helperText={helperText}
              error={errorMessage.length > 0}
            />
          );
        },
      }}
      data={currencies}
      editable={
        permissions?.includes('solution.handbook.update') && {
          onRowAdd: (newCurrency: hb.Currency) => {
            newCurrency.name = allCurrencies[newCurrency.id];
            currencies.push(newCurrency);
            return setState({ currencies });
          },
          onRowUpdate: (newCurrency: hb.Currency, oldCurrency: hb.Currency) => {
            newCurrency.name = allCurrencies[newCurrency.id];
            const index = currencies.indexOf(oldCurrency);
            currencies[index] = newCurrency;
            return setState({ currencies });
          },
          onRowDelete: (oldRecord: hb.Currency) => {
            const index = currencies.indexOf(oldRecord);
            currencies.splice(index, 1);
            return setState({ currencies });
          },
        }
      }
      options={{
        actionsColumnIndex: -1,
        emptyRowsWhenPaging: false,
        showTitle: false,
        paging: false,
        addRowPosition: 'first',
      }}
    />
  );
};

export default CurrencyTable;
